import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { useVoiceCommands } from "@/hooks/useVoiceCommands";
import {
  Mic, MicOff, Volume2, HelpCircle, X,
  AlertTriangle, Heart, Smile, Activity, Phone, Calendar,
  MessageSquare
} from "lucide-react";

export default function VoiceCommandPanel({
  isOpen,
  onClose,
  onPainLevel,
  onMood,
  onSymptom,
  onCheckIn,
  onCallNurse,
  onNextVisit,
  onTalkToGoodwin,
  onVideoCall,
  onStressLevel,
  onSleepHours,
  onConfirm,
  nextVisit
}) {
  const [showHelp, setShowHelp] = useState(false);
  const [commandHistory, setCommandHistory] = useState([]);

  const {
    isListening,
    isSupported,
    isSpeaking,
    transcript,
    lastCommand,
    error,
    startListening,
    stopListening,
    toggleListening,
    speak,
    stopSpeaking
  } = useVoiceCommands({
    enabled: isOpen,
    onPainLevel: (level) => {
      onPainLevel?.(level);
      const msg = level >= 7 
        ? `Pain level ${level} recorded. Alerting your care team.`
        : `Pain level ${level} recorded.`;
      speak(msg);
      addToHistory("pain_level", `Pain level ${level}`);
    },
    onMood: (mood) => {
      onMood?.(mood);
      speak(`Got it, feeling ${mood}.`);
      addToHistory("mood", `Mood: ${mood}`);
    },
    onSymptom: (symptom) => {
      onSymptom?.(symptom);
      speak(`${symptom} symptom noted.`);
      addToHistory("symptom", `Symptom: ${symptom}`);
    },
    onCheckIn: () => {
      onCheckIn?.();
      speak("Opening daily check-in.");
      addToHistory("check_in", "Opened check-in");
    },
    onEmergency: () => {
      speak("Emergency alert sent. Help is on the way.");
      toast.error("Emergency alert sent!", { duration: 10000 });
      addToHistory("emergency", "EMERGENCY");
    },
    onCallNurse: () => {
      onCallNurse?.();
      speak("Connecting to your nurse.");
      addToHistory("call_nurse", "Calling nurse");
    },
    onNextVisit: () => {
      onNextVisit?.();
      if (nextVisit) {
        speak(`Your next visit is ${nextVisit.date} at ${nextVisit.start_time} with ${nextVisit.staff_name}.`);
      } else {
        speak("No upcoming visits scheduled.");
      }
      addToHistory("next_visit", "Next visit info");
    },
    onTalkToGoodwin: () => {
      onTalkToGoodwin?.();
      speak("Hi, I am Goodwin. How can I help?");
      addToHistory("talk_goodwin", "AI companion");
    },
    onVideoCall: () => {
      onVideoCall?.();
      speak("Starting video call.");
      addToHistory("video_call", "Video call");
    },
    onStressLevel: (level) => {
      onStressLevel?.(level);
      speak(`Stress level ${level} recorded.`);
      addToHistory("stress", `Stress level ${level}`);
    },
    onSleepHours: (hours) => {
      onSleepHours?.(hours);
      speak(`${hours} hours of sleep noted.`);
      addToHistory("sleep", `Sleep: ${hours}h`);
    },
    onCancel: () => {
      speak("Cancelled.");
      addToHistory("cancel", "Cancelled");
    },
    onConfirm: () => {
      onConfirm?.();
      speak("Check-in submitted.");
      addToHistory("confirm", "Confirmed");
    },
    onUnknownCommand: (text) => {
      if (text.toLowerCase().includes("help")) {
        speak("You can say: pain level 5, mood is okay, I have nausea, call nurse, or emergency.");
        setShowHelp(true);
      } else {
        speak("Sorry, I did not understand. Say help for options.");
      }
      addToHistory("unknown", text.substring(0, 30));
    }
  });

  const addToHistory = (type, text) => {
    setCommandHistory(prev => [
      { type, text, time: new Date().toLocaleTimeString() },
      ...prev.slice(0, 9)
    ]);
  };

  useEffect(() => {
    if (isOpen && isSupported) {
      const timer = setTimeout(() => {
        speak("Voice mode active. Say help for commands.");
        startListening();
      }, 500);
      return () => clearTimeout(timer);
    }
    return () => {
      stopListening();
      stopSpeaking();
    };
  }, [isOpen, isSupported]);

  const handleClose = () => {
    stopListening();
    speak("Voice mode off.");
    setTimeout(onClose, 1000);
  };

  const getIcon = (type) => {
    const icons = {
      pain_level: <Heart className="w-4 h-4 text-red-500" />,
      mood: <Smile className="w-4 h-4 text-yellow-500" />,
      symptom: <Activity className="w-4 h-4 text-orange-500" />,
      emergency: <AlertTriangle className="w-4 h-4 text-red-600" />,
      call_nurse: <Phone className="w-4 h-4 text-blue-500" />,
      next_visit: <Calendar className="w-4 h-4 text-teal-500" />,
      talk_goodwin: <MessageSquare className="w-4 h-4 text-purple-500" />
    };
    return icons[type] || <Mic className="w-4 h-4 text-slate-400" />;
  };

  if (!isSupported) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Voice Not Supported</DialogTitle>
            <DialogDescription>
              Please use Chrome, Edge, or Safari for voice commands.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg bg-gradient-to-br from-teal-900 to-slate-900 border-teal-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between text-white">
            <span className="flex items-center gap-2">
              <div className={`p-2 rounded-full ${isListening ? "bg-teal-500 animate-pulse" : "bg-slate-700"}`}>
                {isListening ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
              </div>
              Hands-Free Mode
              {isListening && <Badge className="bg-green-500 animate-pulse">LISTENING</Badge>}
            </span>
            <div className="flex items-center gap-2">
              <Button size="icon" variant="ghost" onClick={() => setShowHelp(true)} className="text-slate-300">
                <HelpCircle className="w-5 h-5" />
              </Button>
              <Button size="icon" variant="ghost" onClick={handleClose} className="text-slate-300">
                <X className="w-5 h-5" />
              </Button>
            </div>
          </DialogTitle>
          <DialogDescription className="text-teal-200">
            Speak naturally. Say &quot;help&quot; for commands.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Card className={`${isListening ? "bg-teal-800/50 border-teal-500" : "bg-slate-800/50 border-slate-700"}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-center">
                <Button
                  size="lg"
                  onClick={toggleListening}
                  className={`rounded-full w-20 h-20 ${isListening ? "bg-red-500 hover:bg-red-600" : "bg-teal-500 hover:bg-teal-600"}`}
                  data-testid="voice-toggle-btn"
                >
                  {isListening ? <MicOff className="w-8 h-8" /> : <Mic className="w-8 h-8" />}
                </Button>
              </div>
              
              {isListening && (
                <div className="flex items-center justify-center gap-1 mt-4 h-8">
                  {[...Array(7)].map((_, i) => (
                    <div
                      key={i}
                      className="w-2 bg-teal-400 rounded-full animate-pulse"
                      style={{ height: `${20 + i * 10}%`, animationDelay: `${i * 0.1}s` }}
                    />
                  ))}
                </div>
              )}
              
              {transcript && (
                <div className="mt-4 p-3 bg-slate-900/50 rounded-lg">
                  <p className="text-slate-300 text-sm text-center italic">&quot;{transcript}&quot;</p>
                </div>
              )}
              
              {isSpeaking && (
                <div className="mt-4 flex items-center justify-center gap-2 text-teal-300">
                  <Volume2 className="w-5 h-5 animate-pulse" />
                  <span className="text-sm">Speaking...</span>
                </div>
              )}
            </CardContent>
          </Card>

          {lastCommand && (
            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-center gap-2">
                {getIcon(lastCommand.type)}
                <span className="text-slate-300 text-sm">Last: <span className="text-white font-medium">{lastCommand.text}</span></span>
              </div>
            </div>
          )}

          <div>
            <h4 className="text-slate-400 text-sm mb-2">Recent Commands</h4>
            <ScrollArea className="h-24 rounded-lg bg-slate-800/30 p-2">
              {commandHistory.length === 0 ? (
                <p className="text-slate-500 text-center text-sm py-2">Start speaking!</p>
              ) : (
                <div className="space-y-1">
                  {commandHistory.map((cmd, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs p-1">
                      {getIcon(cmd.type)}
                      <span className="flex-1 text-slate-300">{cmd.text}</span>
                      <span className="text-slate-500">{cmd.time}</span>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" size="sm" onClick={() => { onCheckIn?.(); speak("Opening check-in."); }} className="border-slate-600 text-slate-300">
              <Heart className="w-4 h-4 mr-2" />Check In
            </Button>
            <Button variant="outline" size="sm" onClick={() => { onCallNurse?.(); speak("Calling nurse."); }} className="border-slate-600 text-slate-300">
              <Phone className="w-4 h-4 mr-2" />Call Nurse
            </Button>
          </div>

          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
              <AlertTriangle className="w-4 h-4 inline mr-2" />Error: {error}
            </div>
          )}
        </div>

        <Dialog open={showHelp} onOpenChange={setShowHelp}>
          <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>Voice Commands</DialogTitle>
            </DialogHeader>
            <ScrollArea className="h-64">
              <div className="space-y-3 p-2 text-sm">
                <div>
                  <h4 className="text-teal-400 font-semibold">Check-In</h4>
                  <ul className="text-slate-300 space-y-1 mt-1">
                    <li>&quot;Pain level 5&quot;</li>
                    <li>&quot;My mood is okay&quot;</li>
                    <li>&quot;I have nausea&quot;</li>
                    <li>&quot;Check in&quot; / &quot;Submit&quot;</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-teal-400 font-semibold">Actions</h4>
                  <ul className="text-slate-300 space-y-1 mt-1">
                    <li>&quot;Call my nurse&quot;</li>
                    <li>&quot;Next visit&quot;</li>
                    <li>&quot;Video call&quot;</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-red-400 font-semibold">Emergency</h4>
                  <ul className="text-slate-300 mt-1">
                    <li>&quot;Emergency&quot; or &quot;Help&quot;</li>
                  </ul>
                </div>
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
